"use client";

import { useState, useCallback } from "react";
import { NetworkPulse } from "./NetworkPulse";
import { CommandCenter } from "./CommandCenter";
import { useSoundEngine, type SoundType } from "./SoundEngine";

/**
 * AmbientWrapper — Global layout wrapper that layers:
 * 1. NetworkPulse (living background)
 * 2. CommandCenter (floating dock)
 * 3. SoundEngine (procedural UI sounds)
 *
 * This component manages shared state (sound enabled/disabled)
 * and passes it down to CommandCenter.
 */

export function AmbientWrapper({ children }: { children: React.ReactNode }) {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const { playSound, setEnabled } = useSoundEngine();

  const handleToggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      setEnabled(next);
      return next;
    });
  }, [setEnabled]);

  const handlePlaySound = useCallback(
    (type: SoundType) => {
      playSound(type);
    },
    [playSound]
  );

  return (
    <>
      {/* Living background — fixed, behind everything */}
      <NetworkPulse />

      {/* Page content */}
      {children}

      {/* Floating command center — fixed, on top of everything */}
      <CommandCenter
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onPlaySound={handlePlaySound}
      />
    </>
  );
}
